import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./Notifications.css";
import { API_URL } from "../../utils/config";
import { useContext } from "react";
import AuthContext from "../../contexts/AuthProvider";
const baseUrl = API_URL;

const Notifications = () => {
  const { t } = useTranslation("navbar");
  const [notificationsData, setNotificationsData] = useState([]);
  const [visibleCount, setVisibleCount] = useState(8); // Number of notifications to display initially
  const { isAuthenticated } = useContext(AuthContext);
  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };
  // Fetch notifications from the API
  const fetchNotifications = async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      const response = await fetch(`${baseUrl}/api/v1/notifications/venue`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });
      if (!response.ok) {
        console.error("Failed to fetch notifications");
        return;
      }
      const data = await response.json();
      const notifications = Array.isArray(data)
        ? data
        : data.notifications || data.data || [];
      setNotificationsData(notifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Poll for new notifications every 30 seconds.
  useEffect(() => {
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Load more notifications
  const handleLoadMore = () => {
    setVisibleCount((prevCount) => prevCount + 8);
  };
  //  loop over unread notifications and mark all as read
  // PATCH /api/v1/notifications/venue/read-all

  const handleMarkAllAsRead = async () => {
    try {
      const response = await fetch(
        `${baseUrl}/api/v1/notifications/venue/read-all`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        }
      );
      if (!response.ok) {
        console.error("Failed to mark all notifications as read");
        return;
      }
      // Refresh notifications after marking as read
      fetchNotifications();
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };
const handleMarkRead = (id) => async () => {
    try {
      const response = await fetch(
        `${baseUrl}/api/v1/notifications/venue/${id}/read`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        }
      );
      if (!response.ok) {
        console.error(`Failed to mark notification ${id} as read`);
        return;
      }
      // Refresh notifications after marking as read
      fetchNotifications();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };
  const visibleNotifications = notificationsData.slice(0, visibleCount);
  const hasUnreadNotifications = notificationsData.some((n) => n.is_read === 0);

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <h1 className="notifications-title">{t("Notifications.title")}</h1>
        {hasUnreadNotifications && (
          <button className="mark-all-read-btn" onClick={handleMarkAllAsRead}>
            <span className="mark-as-read-text">
              {t("Notifications.markAllAsRead")}
            </span>

            <svg
              className="check-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.56512 12.4066C4.50105 12.3301 4.42237 12.2672 4.33369 12.2215C4.24501 12.1759 4.14811 12.1483 4.04865 12.1406C3.9492 12.1328 3.8492 12.1449 3.75449 12.1763C3.65979 12.2076 3.5723 12.2576 3.49714 12.3232C3.42197 12.3888 3.36064 12.4687 3.31675 12.5583C3.27285 12.6478 3.24727 12.7453 3.24149 12.8449C3.23572 12.9445 3.24987 13.0442 3.28311 13.1382C3.31636 13.2323 3.36804 13.3188 3.43512 13.3926L4.56512 12.4066ZM7.14312 16.4996L6.57812 16.9926C6.64852 17.0732 6.73536 17.1379 6.83281 17.1822C6.93027 17.2265 7.03607 17.2494 7.14312 17.2494C7.25016 17.2494 7.35597 17.2265 7.45342 17.1822C7.55088 17.1379 7.63772 17.0732 7.70812 16.9926L7.14312 16.4996ZM15.5651 7.99261C15.6322 7.91877 15.6839 7.8323 15.7171 7.73824C15.7504 7.64419 15.7645 7.54445 15.7587 7.44486C15.753 7.34527 15.7274 7.24784 15.6835 7.15826C15.6396 7.06868 15.5783 6.98875 15.5031 6.92317C15.4279 6.85758 15.3404 6.80765 15.2457 6.77629C15.151 6.74494 15.051 6.73279 14.9516 6.74056C14.8521 6.74833 14.7552 6.77586 14.6665 6.82155C14.5779 6.86723 14.4992 6.93014 14.4351 7.00661L15.5651 7.99261ZM10.5061 11.5066C10.38 11.6571 10.318 11.851 10.3333 12.0467C10.3486 12.2424 10.44 12.4243 10.5879 12.5534C10.7358 12.6825 10.9284 12.7484 11.1244 12.737C11.3204 12.7257 11.5041 12.6379 11.6361 12.4926L10.5061 11.5066ZM9.67212 14.7426C9.7392 14.6688 9.79087 14.5823 9.82412 14.4882C9.85737 14.3942 9.87152 14.2945 9.86574 14.1949C9.85997 14.0953 9.83438 13.9978 9.79049 13.9083C9.74659 13.8187 9.68527 13.7388 9.6101 13.6732C9.53494 13.6076 9.44744 13.5576 9.35274 13.5263C9.25804 13.4949 9.15804 13.4828 9.05858 13.4906C8.95913 13.4983 8.86223 13.5259 8.77354 13.5715C8.68486 13.6172 8.60618 13.6801 8.54212 13.7566L9.67212 14.7426ZM3.43512 13.3926L6.57812 16.9926L7.70812 16.0066L4.56512 12.4066L3.43512 13.3926ZM7.70812 16.9926L9.67212 14.7426L8.54212 13.7566L6.57812 16.0066L7.70812 16.9926ZM11.6361 12.4926L13.6011 10.2426L12.4711 9.25661L10.5061 11.5066L11.6361 12.4926ZM13.6011 10.2426L15.5651 7.99261L14.4351 7.00661L12.4711 9.25661L13.6011 10.2426Z"
                fill="#5007C5"
              />
              <path
                d="M20 7.5625L15.714 12.0625M11 15.9995L11.429 16.5625L13.572 14.3125"
                stroke="#5007C5"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      {notificationsData.length === 0 ? (
        <div className="empty-notifications">
          <p>{t("Notifications.noNotifications")}</p>
        </div>
      ) : (
        visibleNotifications.map((notification) => (
          <div className="notification-item" key={notification.id} onClick={handleMarkRead(notification.id)}
           // if unread make it cursor pointer
           style={{ cursor: notification.is_read===1 ? "default" : "pointer" }}
          >
            <div className="notification-icon">
              <img
                src="notificationItem.svg"
                alt={t("Notifications.notificationIconAlt")}
              />
            </div>
            <div className="notification-info">
              <div className="message-title">
                <span className="notification-message">
                  <strong className="notification-title">
                    {notification.title} {" "}
                  </strong>
                  {notification.message}
                </span>
              </div>
              <div className="notification-date">
                {new Date(notification.created_at).toLocaleString()}
              </div>
            </div>
          </div>
        ))
      )}
      {notificationsData.length > visibleCount && (
        <button className="load-more-btn" onClick={handleLoadMore}>
          {t("Notifications.loadMore")}
        </button>
      )}
    </div>
  );
};

export default Notifications;
